import { useEffect, useState, useRef } from "react";
import { IAnalysedProperty, IPropertyDetail } from "../../../../../interface/addAnalyser";
import { useAnalysedPropertyContext } from "../AnalysedPropertyContext";
import { REIButton, REIDialoge, REIDropDown, REIInputNumber, REILinkButton } from "../../../../../components/ui";
import InputSlider from "../../../../../components/ui/slider";
import { IMemberEntity } from "../../../../../interface/analysed";
import EntityOwnerShip from "../AddEntityOwnership";
import { fetchMemeberDetail } from "../../../../../store/slice/analyserSlice";
import { AppDispatch } from "../../../../../store";
import { useDispatch } from "react-redux";
import MyEntityGoalsIndex from "../../../../PortfolioTracker/MyEntityAndGoals";
import { FilterSelectionProvider } from "../../../../PortfolioTracker/FilterSelectionContext";

interface IAssignEntity {
  setIsValid: (isValid: boolean) => void;
  updateProperty: (updated: Partial<IAnalysedProperty>) => void;
  memberEntities: IMemberEntity[];
}

const AssignEntity: React.FC<IAssignEntity> = ({ setIsValid, updateProperty, memberEntities }) => {
  const { analysedProperty } = useAnalysedPropertyContext();
  const [propertyDetails, setPropertyDetails] = useState<IPropertyDetail | undefined>(undefined);
  const PropertyEntities = useRef<any[]>([]);
  const [showEntityOwnershipDialog, setShowEntityOwnershipDialog] = useState(false);
  const [showEntityGoalsDialog, setShowEntityGoalsDialog] = useState(false);
  const [reset, setReset] = useState<boolean>(false);
  const dispatch: AppDispatch = useDispatch();
  const [errors, setErrors] = useState<{ MemberEntityId?: string }>({});

  useEffect(() => {
    if (analysedProperty && !reset) {
      setPropertyDetails(analysedProperty.PropertyDetail);
      setPropertyEntityNames();
      setReset(true);
    }
  }, [analysedProperty]);

  useEffect(() => {
    const fetchData = async () => {
      if (!memberEntities || memberEntities.length === 0) {
        await dispatch(fetchMemeberDetail());
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (memberEntities && memberEntities.length > 0) {
      const currentEntity = memberEntities.find((x) => x.MemberEntityId == Number(propertyDetails?.MemberEntityId));
      setPropertyEntityNames();
      const isNewAssignment = !propertyDetails?.MemberEntityId || propertyDetails?.MemberEntityId === "0";
      const needsReset = !currentEntity || isNewAssignment;
      if (needsReset) {
        setPropertyDetails((prevState) => {
          if (!prevState) return undefined;
          return {
            ...prevState,
            MemberEntityId: String(memberEntities[0].MemberEntityId),
            ...(isNewAssignment ? { PortionOwned: "100" } : {}),
          } as IPropertyDetail;
        });
      }
    }
  }, [memberEntities]);

  const validate = () => {
    let isValid = true;
    const newErrors: { MemberEntityId?: string } = {};

    if (
      propertyDetails?.MemberEntityId === null ||
      propertyDetails?.MemberEntityId === undefined ||
      propertyDetails?.MemberEntityId === "0"
    ) {
      newErrors.MemberEntityId = "Entity is required.";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  useEffect(() => {
    if (propertyDetails) {
      const isValid = validate();
      setIsValid(isValid);
      updateProperty({
        ...analysedProperty,
        PropertyDetail: propertyDetails,
      });
    }
  }, [propertyDetails]);

  const setValues = (name: string, value: string | number) => {
    setPropertyDetails((prevState) => {
      if (!prevState) return undefined;
      const updatedExpense = {
        ...prevState,
        [name]: value !== undefined ? value : 0,
      };
      return updatedExpense;
    });
  };

  const setPropertyEntityNames = () => {
    if (memberEntities) {
      let memeberEntitiesValues: any[] = [];
      memberEntities.map((entities) => {
        memeberEntitiesValues.push({
          EntityName: entities.EntityName,
          EntityValue: entities.MemberEntityId.toString(),
        });
      });
      PropertyEntities.current = memeberEntitiesValues;
    }
  };

  const hideEntityOwnershipDialog = async () => {
    await dispatch(fetchMemeberDetail());
    setShowEntityOwnershipDialog(false);
  };

  return (
    <div>
      <REIDialoge
        showHeader={true}
        header={`Add a new entity ownership structure`}
        visible={showEntityOwnershipDialog}
        handleEditClick={hideEntityOwnershipDialog}
        position="top"
        customClass="update-statistics-dialog"
        style={{ width: "55vw" }}
        contentComponent={<EntityOwnerShip closeDialogue={hideEntityOwnershipDialog} />}
      ></REIDialoge>
      <REIDialoge
        showHeader={true}
        header="My Entity & Goals"
        visible={showEntityGoalsDialog}
        handleEditClick={async () => {
          setShowEntityGoalsDialog(false);
          try {
            await dispatch(fetchMemeberDetail());
          } catch (e) {
            console.error("Failed to fetch member detail:", e);
          }
        }}
        position="top"
        style={{ width: "90vw", maxHeight: "90vh" }}
        customClass="entity-goals-dialog"
        contentComponent={
          <FilterSelectionProvider
            initialEntityId={propertyDetails?.MemberEntityId ? Number(propertyDetails.MemberEntityId) : 0}
          >
            <div style={{ height: "75vh", overflow: "auto" }}>
              <MyEntityGoalsIndex />
            </div>
          </FilterSelectionProvider>
        }
      ></REIDialoge>
      <div className="relative rounded-lg pl-3 pr-1 py-3 border-solid border border-gray-300 bg-white mt-2">
        <div className="default-profile-header-container">
          <h3 className="text-[var(--primary-color)] mt-0 m-1 text-lg">Assign an entity</h3>
        </div>
        <div className="p-4">
          <div className="flex flex-wrap mb-4">
            <div className="w-full md:w-1/2 p-2">
              <div className="flex items-center justify-evenly">
                <label className="w-1/2 text-left" htmlFor="Rates">
                  This property belongs to entity:
                </label>
                <REIDropDown
                  id="asent-drop-entity"
                  data={PropertyEntities.current}
                  onSelectionChange={(e) => setValues("MemberEntityId", Array.isArray(e.value) ? e.value[0] : e.value)}
                  placeholder="Select"
                  value={propertyDetails?.MemberEntityId}
                  optionValue="EntityValue"
                  label="EntityName"
                  className={`w-1/2${errors.MemberEntityId ? " p-invalid" : ""}`}
                ></REIDropDown>
              </div>
              <div className="justify-self-end">
                {errors.MemberEntityId && (
                  <small className="p-error" style={{ color: "red" }}>
                    {errors.MemberEntityId}
                  </small>
                )}
              </div>
            </div>
            <div className="w-full md:w-1/2 p-2">
              <div className="flex">
                <REIButton
                  id="asent-btn-createentity"
                  label="Create a new entity"
                  classNames="p-button-link p-0 pt-2 text-wrap"
                  onClick={() => {
                    setShowEntityOwnershipDialog(true);
                  }}
                />
              </div>
            </div>
          </div>

          <div className="flex flex-wrap mb-4">
            <div className="w-full md:w-1/2 p-2">
              <div className="flex items-center justify-evenly">
                <label className="w-1/3 text-left" htmlFor="entityName">
                  Entity selected :
                </label>
                <label className="w-2/3 text-left" htmlFor="PortionOwned">
                  Entity Owns
                </label>
              </div>
            </div>
          </div>
          <div className="w-full md:w-1/2 p-2 justify-evenly">
            <div className="flex flex-row items-center align-middle">
              <div className="w-1/3 flex items-center" id="entityName">
                {memberEntities && (
                  <>
                    <label className="text-left">
                      <b>
                        {
                          memberEntities.find((x) => x.MemberEntityId == Number(propertyDetails?.MemberEntityId))
                            ?.EntityName
                        }
                      </b>
                    </label>
                    <REIButton
                      id="asent-btn-viewentity"
                      icon="pi pi-user-edit"
                      label="View"
                      classNames="p-button-link ml-2"
                      onClick={() => {
                        setShowEntityGoalsDialog(true);
                      }}
                    />
                  </>
                )}
              </div>
              <div className="w-2/3 flex flex-row content-center items-center">
                <InputSlider
                  id="asent-sldr-portionowned"
                  value={propertyDetails?.PortionOwned ? Number(propertyDetails.PortionOwned) : 0}
                  onChange={(e) => setValues("PortionOwned", Array.isArray(e.value) ? e.value[0] : e.value)}
                  classNames="w-full mr-3"
                  min={1}
                  max={100}
                  step={1}
                />
                <REIInputNumber
                  id="asent-num-portionowned"
                  value={propertyDetails?.PortionOwned ? Number(propertyDetails.PortionOwned) : 0}
                  onValueChange={(e) => setValues("PortionOwned", e.target.value)}
                  useGrouping={true}
                  min={1}
                  max={100}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignEntity;
